import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Form } from 'react-bootstrap';
import { ArrowLeft } from 'lucide-react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Select from 'react-select';
import { userService, companyService, teamService, departmentService } from '../services/apiService';
import { usePermissions } from '../context/PermissionsContext';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useAuthToken } from '../hooks/useAuthToken';
import { useConfirm } from '../components/ConfirmDialog';
import { LoadingState, ErrorState, isNotFound, serverMessage } from '../components/PageState';
import UserNotifications from '../components/user/UserNotifications';
import UserNotificationHistory from '../components/user/UserNotificationHistory';
import { formatDate, plural, roleLabel, authTypeLabel, EMPTY } from '../utils/format';

const ROLES = ['BASIC', 'COMPANY_ADMIN', 'TEAM_ADMIN', 'DEPARTMENT_ADMIN', 'OFFICE_ADMIN'];
const AUTH_TYPES = ['TT', 'GG', 'MS'];

const SPECIAL_ROLES = [
  { key: 'firstAider', label: 'First aider' },
  { key: 'fireWarden', label: 'Fire warden' },
  { key: 'mentalHealthAider', label: 'Mental health aider' },
  { key: 'keyHolder', label: 'Key holder' },
];

// What saving a move to another company does, matching the backend's UserCompanyResetService
const MOVE_RESETS = [
  'Sets their role to Basic',
  'Clears their team and department',
  'Removes them from workgroups, and stops them following any teams or workgroups',
  'Removes their line manager and direct report links',
  'Resets their default office',
  'Deletes their favourite desks and offices',
];

const yesNo = (value) => (value ? 'Yes' : 'No');

// The editable fields of a user as the API returns it
const toForm = (user) => ({
  firstname: user.firstname || '',
  lastname: user.lastname || '',
  email: user.email || '',
  jobTitle: user.jobTitle || '',
  role: user.role || 'BASIC',
  authType: user.authType || 'TT',
  companyId: user.companyId || null,
  teamId: user.teamId || null,
  departmentId: user.departmentId ?? null,
  accessPermitted: !!user.accessPermitted,
  emailVerified: !!user.emailVerified,
  parkingEnabled: !!user.parkingEnabled,
  firstAider: !!user.firstAider,
  fireWarden: !!user.fireWarden,
  mentalHealthAider: !!user.mentalHealthAider,
  keyHolder: !!user.keyHolder,
});

// react-select styles as CSS variables, so the selects follow a theme switch without a re-render
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '32px',
    backgroundColor: state.isDisabled ? 'var(--bg-subtle)' : 'var(--bg-default)',
    borderColor: state.isFocused ? 'var(--color-accent-emphasis)' : 'var(--border-default)',
    boxShadow: state.isFocused ? '0 0 0 3px var(--color-accent-muted)' : 'none',
    borderRadius: '8px',
    fontSize: '14px',
    '&:hover': {
      borderColor: state.isFocused ? 'var(--color-accent-emphasis)' : 'var(--border-default)',
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'var(--bg-overlay)',
    border: '1px solid var(--border-default)',
    boxShadow: 'var(--shadow-floating)',
    borderRadius: '8px',
    zIndex: 9999,
    fontSize: '14px',
  }),
  menuList: (base) => ({
    ...base,
    padding: '4px',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'var(--color-accent-emphasis)'
      : state.isFocused
      ? 'var(--bg-inset)'
      : 'transparent',
    color: state.isSelected ? 'var(--fg-onEmphasis)' : 'var(--fg-default)',
    cursor: 'pointer',
    borderRadius: '6px',
    padding: '6px 8px',
    fontSize: '14px',
    '&:active': {
      backgroundColor: 'var(--color-accent-emphasis)',
    },
  }),
  singleValue: (base, state) => ({
    ...base,
    color: state.isDisabled ? 'var(--fg-muted)' : 'var(--fg-default)',
  }),
  input: (base) => ({
    ...base,
    color: 'var(--fg-default)',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--fg-muted)',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: 'var(--border-default)',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: 'var(--fg-muted)',
    '&:hover': {
      color: 'var(--fg-default)',
    },
  }),
  clearIndicator: (base) => ({
    ...base,
    color: 'var(--fg-muted)',
    '&:hover': {
      color: 'var(--color-danger-fg)',
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    color: 'var(--fg-muted)',
    fontSize: '14px',
  }),
};

const UserDetail = () => {
  const { canEdit } = usePermissions();
  const isMobile = useIsMobile();
  const { id } = useParams();
  const navigate = useNavigate();
  const refreshToken = useAuthToken();
  const { confirm, confirmDialog } = useConfirm();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [form, setForm] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joinStatus, setJoinStatus] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [teams, setTeams] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Teams and departments of the user's current company, for the names in Details and the form
  const companyId = user?.companyId;
  useEffect(() => {
    if (!companyId) {
      setTeams([]);
      setDepartments([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        await refreshToken();
        const [teamsResponse, departmentsResponse] = await Promise.allSettled([
          teamService.getTeamsByCompany(companyId),
          departmentService.getDepartmentsByCompany(companyId),
        ]);
        if (cancelled) return;
        setTeams(teamsResponse.status === 'fulfilled' ? teamsResponse.value.data : []);
        setDepartments(departmentsResponse.status === 'fulfilled' ? departmentsResponse.value.data : []);
      } catch (err) {
        console.error('Failed to load teams and departments:', err);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [companyId, refreshToken]);

  // Only admins see the edit form, so only they need every company for the company picker
  useEffect(() => {
    if (!canEdit) return;
    const load = async () => {
      try {
        await refreshToken();
        const response = await companyService.getAllCompanies();
        setCompanies(response.data);
      } catch (err) {
        console.error('Failed to load companies:', err);
      }
    };
    load();
  }, [canEdit, refreshToken]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      await refreshToken();
      const response = await userService.getUser(id);
      setUser(response.data);
      setForm(toForm(response.data));
    } catch (err) {
      setLoadError(err);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Back to wherever the user was opened from, or to user search when the page was opened directly
  const goBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/user-search');
  };

  const fullName = user ? (`${user.firstname || ''} ${user.lastname || ''}`.trim() || user.email || 'User') : '';
  const movePending = !!(user && form?.companyId && form.companyId !== user.companyId);
  const dirty = !!(user && form) && JSON.stringify(form) !== JSON.stringify(toForm(user));

  const handleFieldChange = (field, value) => {
    setSaveStatus(null);
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCompanyChange = (option) => {
    // The select cannot be cleared, and an empty company must never be saved
    if (!option) return;
    setSaveStatus(null);
    setForm((prev) => (option.value === user.companyId
      // Back to the current company: nothing resets, so restore what the user has
      ? { ...prev, companyId: option.value, role: user.role || 'BASIC', teamId: user.teamId || null, departmentId: user.departmentId ?? null }
      // A move resets these when saved, so show the outcome and lock them until then
      : { ...prev, companyId: option.value, role: 'BASIC', teamId: null, departmentId: null }));
  };

  const companyName = (companyIdToFind) =>
    companies.find((company) => company.id === companyIdToFind)?.name;

  const handleSaveUser = async () => {
    if (movePending) {
      const target = companyName(form.companyId) || 'the new company';
      const confirmed = await confirm({
        title: `Move ${fullName} to ${target}?`,
        body: (
          <>
            <p>
              Saving moves this user out of {user.companyName || 'their current company'}. It also:
            </p>
            <ul className="mb-0">
              {MOVE_RESETS.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </>
        ),
        confirmLabel: 'Move user',
        variant: 'danger',
      });
      if (!confirmed) return;
    }

    try {
      setIsSaving(true);
      setSaveStatus(null);
      await refreshToken();
      const response = await userService.updateUser(id, { ...user, ...form });
      setUser(response.data);
      setForm(toForm(response.data));
      setSaveStatus({
        type: 'success',
        message: movePending ? `Moved to ${response.data.companyName || 'the new company'}` : 'Saved',
      });
    } catch (err) {
      setSaveStatus({ type: 'error', message: serverMessage(err, 'Could not save the changes. Try again.') });
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // Moves the signed-in staff member's own account into this user's company, as its company admin
  const handleJoinCompany = async () => {
    try {
      setJoining(true);
      setJoinStatus(null);
      await refreshToken();
      await userService.joinCompany(user.companyId);
      setJoinStatus({ type: 'success', message: `Your account is now an admin in ${user.companyName || 'this company'}` });
    } catch (err) {
      setJoinStatus({ type: 'error', message: serverMessage(err, 'Could not join this company. Try again.') });
      console.error(err);
    } finally {
      setJoining(false);
    }
  };

  const backButton = (
    <Button variant="outline-secondary" onClick={goBack}>
      <ArrowLeft size={14} style={{ verticalAlign: '-2px', marginRight: 4 }} />Back
    </Button>
  );

  if (loading || !user || !form) {
    return (
      <Container fluid className="py-4">
        <div className="mb-3">{backButton}</div>
        {loading ? (
          <LoadingState label="Loading user" />
        ) : isNotFound(loadError) ? (
          <Alert variant="warning">User not found</Alert>
        ) : (
          <ErrorState message={serverMessage(loadError, 'Could not load this user.')} onRetry={fetchUser} />
        )}
      </Container>
    );
  }

  // One click, no confirmation: it moves the signed-in staff member's own account, not this user
  const joinLabel = user.companyId ? `Join ${user.companyName || 'this company'} as admin` : 'Join company as admin';
  const joinButton = canEdit && (
    <Button
      variant="outline-primary"
      onClick={handleJoinCompany}
      disabled={joining || !user.companyId}
      title={user.companyId
        ? 'Moves your own Team Today account into this company as a company admin'
        : 'This user is not in a company'}
      className={isMobile ? undefined : 'text-truncate'}
      style={isMobile ? undefined : { maxWidth: 320 }}
    >
      {joining && <Spinner as="span" animation="border" size="sm" aria-hidden="true" className="me-2" />}
      {joinLabel}
    </Button>
  );
  const joinResult = joinStatus && (
    <span className={`save-status is-${joinStatus.type === 'success' ? 'success' : 'error'}`} role="status">
      {joinStatus.message}
    </span>
  );

  const teamName = user.teamId
    ? teams.find((team) => team.id === user.teamId)?.name || <code>{user.teamId}</code>
    : EMPTY;
  const departmentName = user.departmentId
    ? departments.find((department) => department.id === user.departmentId)?.name || <code>{user.departmentId}</code>
    : EMPTY;
  const specialRoles = SPECIAL_ROLES.filter(({ key }) => user[key]).map(({ label }) => label);

  // Until the companies have loaded the picker still shows the current company
  const companyOptions = companies.length > 0
    ? companies.map((company) => ({ value: company.id, label: company.name }))
    : user.companyId ? [{ value: user.companyId, label: user.companyName || user.companyId }] : [];
  const selectedCompanyOption = companyOptions.find((option) => option.value === form.companyId) || null;
  const teamOptions = teams.map((team) => ({ value: team.id, label: team.name }));
  const selectedTeamOption = movePending ? null : teamOptions.find((option) => option.value === form.teamId) || null;
  const departmentOptions = departments.map((department) => ({ value: department.id, label: department.name }));
  const selectedDepartmentOption = movePending
    ? null
    : departmentOptions.find((option) => option.value === form.departmentId) || null;

  // Deleted users stay read-only, and nothing changes while a save is running
  const formLocked = !canEdit || user.deleted || isSaving;

  return (
    <Container fluid className="py-4">
      {confirmDialog}
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            {backButton}
            {user.companyId && (
              <Button as={Link} to={`/companies/${user.companyId}`} variant="outline-primary">
                Go to company
              </Button>
            )}
            {/* On phones Join sits with the other buttons instead of on a line of its own */}
            {isMobile && joinButton}
            {isMobile && joinResult}
          </div>
          {/* On phones the name takes its own line and the badges wrap below it. Badges only for exceptions. */}
          <div className={isMobile ? 'd-flex flex-wrap align-items-center gap-2' : 'd-flex align-items-center gap-3'}>
            <h1 className={isMobile ? 'w-100 mb-0' : 'mb-0'}>{fullName}</h1>
            {user.deleted && <Badge bg="danger">Deleted</Badge>}
            {!user.accessPermitted && <Badge bg="secondary">Access denied</Badge>}
            {!user.emailVerified && <Badge bg="warning">Unverified</Badge>}
          </div>
          {user.deleted && (
            <p className="text-muted mt-2 mb-0">Deleted on {formatDate(user.deletedAt)}</p>
          )}
        </Col>
        {!isMobile && joinButton && (
          <Col xs="auto" className="d-flex flex-column align-items-end justify-content-end gap-1 pb-1">
            {joinButton}
            {joinResult}
          </Col>
        )}
      </Row>

      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-light">
          <h5 className="mb-0">Details</h5>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col lg={6}>
              <dl className="kv-list">
                <dt>Email</dt>
                <dd>{user.email || EMPTY}</dd>
                <dt>Job title</dt>
                <dd>{user.jobTitle || EMPTY}</dd>
                <dt>Company</dt>
                <dd>
                  {user.companyId ? (
                    <Link to={`/companies/${user.companyId}`}>{user.companyName || user.companyId}</Link>
                  ) : (
                    EMPTY
                  )}
                </dd>
                <dt>Team</dt>
                <dd>{teamName}</dd>
                <dt>Department</dt>
                <dd>{departmentName}</dd>
                <dt>Role</dt>
                <dd>{roleLabel(user.role)}</dd>
                <dt>Created</dt>
                <dd>{formatDate(user.createdAt)}</dd>
                <dt>User ID</dt>
                <dd><code>{user.id}</code></dd>
              </dl>
            </Col>
            <Col lg={6}>
              <dl className="kv-list">
                <dt>Sign-in method</dt>
                <dd>{authTypeLabel(user.authType)}</dd>
                <dt>Access permitted</dt>
                <dd>{yesNo(user.accessPermitted)}</dd>
                <dt>Email verified</dt>
                <dd>{yesNo(user.emailVerified)}</dd>
                <dt>Parking</dt>
                <dd>{yesNo(user.parkingEnabled)}</dd>
                <dt>Special roles</dt>
                <dd>{specialRoles.length > 0 ? specialRoles.join(', ') : 'None'}</dd>
                <dt>Holiday allowance</dt>
                <dd>{user.holidayAllowance != null ? plural(user.holidayAllowance, 'day') : EMPTY}</dd>
              </dl>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <UserNotifications user={user} />

      <UserNotificationHistory user={user} />

      {canEdit && (
        <Card className="shadow-sm">
          <Card.Header className="bg-light">
            <h5 className="mb-0">Edit user</h5>
          </Card.Header>
          <Card.Body>
            {user.deleted && (
              <Alert variant="secondary">This user is deleted, so their details cannot be changed.</Alert>
            )}
            <Form onSubmit={(e) => { e.preventDefault(); if (dirty && !formLocked) handleSaveUser(); }}>
              <fieldset disabled={formLocked}>
                <h6 className="mb-3 text-secondary">Profile</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="firstname">
                      <Form.Label>First name</Form.Label>
                      <Form.Control
                        type="text"
                        value={form.firstname}
                        onChange={(e) => handleFieldChange('firstname', e.target.value)}
                        placeholder="Enter first name"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="lastname">
                      <Form.Label>Last name</Form.Label>
                      <Form.Control
                        type="text"
                        value={form.lastname}
                        onChange={(e) => handleFieldChange('lastname', e.target.value)}
                        placeholder="Enter last name"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="email">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={form.email}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                        placeholder="Enter email"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="jobTitle">
                      <Form.Label>Job title</Form.Label>
                      <Form.Control
                        type="text"
                        value={form.jobTitle}
                        onChange={(e) => handleFieldChange('jobTitle', e.target.value)}
                        placeholder="Enter job title"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3 text-secondary">Company and role</h6>
                <Form.Group className="mb-3" controlId="company">
                  <Form.Label>Company</Form.Label>
                  <Select
                    inputId="company"
                    options={companyOptions}
                    value={selectedCompanyOption}
                    onChange={handleCompanyChange}
                    placeholder="Search and select a company..."
                    isSearchable
                    isDisabled={formLocked}
                    noOptionsMessage={() => 'No companies found'}
                    styles={selectStyles}
                  />
                  <Form.Text className="text-muted">
                    Moving a user to another company resets their role, team, department and workgroups, and deletes their favourites.
                  </Form.Text>
                </Form.Group>

                {movePending && (
                  <Alert variant="warning" className="py-2">
                    Role, team and department reset when you save this move. Set new ones after saving.
                  </Alert>
                )}

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="role">
                      <Form.Label>Role</Form.Label>
                      <Form.Select
                        value={form.role}
                        onChange={(e) => handleFieldChange('role', e.target.value)}
                        disabled={movePending}
                      >
                        {ROLES.map((role) => (
                          <option key={role} value={role}>{roleLabel(role)}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="team">
                      <Form.Label>Team</Form.Label>
                      <Select
                        inputId="team"
                        options={teamOptions}
                        value={selectedTeamOption}
                        onChange={(option) => handleFieldChange('teamId', option ? option.value : null)}
                        placeholder={movePending ? 'Set after the move' : 'Search and select a team...'}
                        isClearable
                        isSearchable
                        noOptionsMessage={() => 'No teams found'}
                        styles={selectStyles}
                        isDisabled={formLocked || movePending || !form.companyId}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="department">
                      <Form.Label>Department</Form.Label>
                      <Select
                        inputId="department"
                        options={departmentOptions}
                        value={selectedDepartmentOption}
                        onChange={(option) => handleFieldChange('departmentId', option ? option.value : null)}
                        placeholder={movePending ? 'Set after the move' : 'Search and select a department...'}
                        isClearable
                        isSearchable
                        noOptionsMessage={() => 'No departments found'}
                        styles={selectStyles}
                        isDisabled={formLocked || movePending || !form.companyId}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3 text-secondary">Sign-in access</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3" controlId="authType">
                      <Form.Label>Sign-in method</Form.Label>
                      <Form.Select
                        value={form.authType}
                        onChange={(e) => handleFieldChange('authType', e.target.value)}
                      >
                        {AUTH_TYPES.map((authType) => (
                          <option key={authType} value={authType}>{authTypeLabel(authType)}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="accessPermitted"
                        label="Access permitted"
                        checked={form.accessPermitted}
                        onChange={(e) => handleFieldChange('accessPermitted', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="emailVerified"
                        label="Email verified"
                        checked={form.emailVerified}
                        onChange={(e) => handleFieldChange('emailVerified', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <hr className="my-4" />

                <h6 className="mb-3 text-secondary">Parking and special roles</h6>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="switch"
                        id="parkingEnabled"
                        label="Parking enabled"
                        checked={form.parkingEnabled}
                        onChange={(e) => handleFieldChange('parkingEnabled', e.target.checked)}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  {SPECIAL_ROLES.map(({ key, label }) => (
                    <Col md={6} key={key}>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="switch"
                          id={key}
                          label={label}
                          checked={form[key]}
                          onChange={(e) => handleFieldChange(key, e.target.checked)}
                        />
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </fieldset>

              <div className="form-sticky-actions d-flex flex-wrap align-items-center gap-3">
                <Button type="submit" variant="primary" disabled={!dirty || formLocked}>
                  {isSaving ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    'Save changes'
                  )}
                </Button>
                {saveStatus ? (
                  <span className={`save-status is-${saveStatus.type === 'success' ? 'success' : 'error'}`} role="status">
                    {saveStatus.message}
                  </span>
                ) : dirty && (
                  <span className="save-status">Unsaved changes</span>
                )}
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default UserDetail;
